import { customCreateMutation, customUpdateMutation } from "@/admin/custom_products/customProductsApi.ts";
import { AInput } from "@/admin/form/Fields.tsx";
import { defaultTz } from "@/admin/utils/time.ts";
import { controllerProps, formPropsToAriaProps } from "@/components/forms/ariaController.tsx";
import { FormField } from "@/components/forms/FormField.tsx";
import { WDateField } from "@/components/forms/WDateField.tsx";
import { WModalForm } from "@/components/forms/WModalForm.tsx";
import { WNumberField } from "@/components/forms/WNumberField.tsx";
import { WSelect } from "@/components/forms/WSelect.tsx";
import { WTextField } from "@/components/forms/WTextField.tsx";
import { WModal } from "@/components/modal/WModal.tsx";
import { siteCurrencyAtom } from "@/config.ts";
import { useApiClient } from "@/modules/api/apiClient.store.ts";
import { useStandardMutation } from "@/modules/api/useStandardMutation.ts";
import { queryParamNumberCast } from "@/utils/cast.ts";
import { type CalendarDateTime, fromAbsolute, toCalendarDateTime, toTimeZone } from "@internationalized/date";
import { apiDatetimeToDate } from "@warrenio/api-spec/conversion";
import type { CustomBillableProduct } from "@warrenio/api-spec/spec.oats.gen";
import { notNull } from "@warrenio/utils/notNull";
import { discardPromise } from "@warrenio/utils/promise/discardPromise";
import { useAtomValue } from "jotai/react";
import type { FocusEvent, ReactNode } from "react";
import { useMemo, useState } from "react";
import { Controller, type SubmitHandler, useForm } from "react-hook-form";

function dateToValue(date: CalendarDateTime | null) {
    return date != null ? date.toDate(defaultTz).toISOString() : undefined;
}

/** @returns Current date if the input is nullish */
function valueToDate(value: string | null | undefined) {
    const date = value ? apiDatetimeToDate(value) : new Date();
    return convertToCalendarDateTime(date);
}

function convertToCalendarDateTime(date: Date) {
    const time = fromAbsolute(date.valueOf(), "UTC");
    return toCalendarDateTime(toTimeZone(time, defaultTz));
}

export function AddProductButton({ button }: { button: ReactNode }) {
    return (
        <WModal button={button}>
            <AddEditProductModalContent />
        </WModal>
    );
}

export function EditProductButton({ item, button }: { item: CustomBillableProduct; button: ReactNode }) {
    return (
        <WModal button={button}>
            <AddEditProductModalContent item={item} />
        </WModal>
    );
}

interface QtyUnit {
    id: string;
    title: string;
}

const qtyUnitItems: QtyUnit[] = [
    { id: "pc", title: "pc" },
    { id: "h", title: "h" },
];

interface AddEditProductInputs {
    description: string;
    billing_account_id: number;
    price_per_unit: number;
    qty_unit: string;
    start_date: CalendarDateTime;
    end_date: CalendarDateTime | null;
}

function AddEditProductModalContent({ item }: { item?: CustomBillableProduct }) {
    //#region Hooks
    const createMutation = useStandardMutation(customCreateMutation);
    const updateMutation = useStandardMutation(customUpdateMutation);
    const api = useApiClient();
    const siteCurrency = useAtomValue(siteCurrencyAtom);

    const placeholder = useMemo(() => convertToCalendarDateTime(new Date()), []);

    const defaultStartDate = valueToDate(item?.start_date);
    const defaultEndDate = item?.end_date ? valueToDate(item?.end_date) : null;

    const [error, setError] = useState<string>("");
    //#endregion

    const onSubmit: SubmitHandler<AddEditProductInputs> = async (inputs) => {
        const { description, billing_account_id, price_per_unit, qty_unit, start_date, end_date } = inputs;

        const endDate = dateToValue(end_date);

        if (item) {
            await updateMutation.mutateAsync({
                body: {
                    end_date: endDate ?? "",
                },
                uuid: item.uuid,
            });
        } else {
            const startDate = notNull(dateToValue(start_date), "Start date");

            await createMutation.mutateAsync({
                body: {
                    description,
                    billing_account_id,
                    price_per_unit,
                    qty_unit,
                    start_date: startDate,
                    end_date: endDate ?? "",
                },
            });
        }
    };

    async function getBillingAccount(event: FocusEvent) {
        const target = event.target as HTMLInputElement;
        const value = target.value;
        const response = await api.GET("/payment/admin/credit", {
            params: { query: { billing_account_id: queryParamNumberCast(value) } },
        });

        setError(response.data ? "" : "Billing Account not found");
    }

    const isEditMode = !!item;

    const form = useForm<AddEditProductInputs>({
        mode: "onBlur",
        reValidateMode: "onBlur",

        defaultValues: {
            description: item?.description ?? "",
            billing_account_id: item?.billing_account_id ?? undefined,
            price_per_unit: item?.price_per_unit ?? 0,
            qty_unit: item?.qty_unit ?? qtyUnitItems[0].id,
            start_date: defaultStartDate,
            end_date: defaultEndDate,
        },
    });

    const { control } = form;

    return (
        <WModalForm
            title={isEditMode ? "Edit Custom Billable Product" : "Add New Custom Billable Product"}
            label={isEditMode ? "Edit" : "Add"}
            form={form}
            onSubmit={onSubmit}
        >
            <Controller
                control={control}
                name="description"
                {...controllerProps({ isRequired: true })}
                render={({ field }) => (
                    <WTextField
                        wide
                        autoFocus={!isEditMode}
                        isRequired
                        isReadOnly={isEditMode}
                        headless
                        label="Description"
                        value={field.value}
                        onChange={field.onChange}
                    >
                        <AInput />
                    </WTextField>
                )}
            />

            <Controller
                control={control}
                name="billing_account_id"
                {...controllerProps({ isRequired: true })}
                render={(props) => (
                    <WNumberField
                        wide
                        isReadOnly={isEditMode}
                        headless
                        label="BA ID"
                        {...formPropsToAriaProps(props)}
                        isInvalid={!!error || props.fieldState.invalid}
                        errorMessage={error || props.fieldState.error?.message}
                        onBlur={(e) => {
                            // XXX: This has race conditions when `onBlur` is called several times in a row
                            props.field.onBlur();
                            discardPromise(getBillingAccount(e));
                        }}
                    >
                        <AInput />
                    </WNumberField>
                )}
            />

            <Controller
                control={control}
                name="price_per_unit"
                {...controllerProps({ isRequired: true })}
                render={(props) => (
                    <WNumberField
                        wide
                        isReadOnly={isEditMode}
                        headless
                        label={`Price Per Unit (${siteCurrency})`}
                        {...formPropsToAriaProps(props)}
                    >
                        <AInput />
                    </WNumberField>
                )}
            />

            <Controller
                control={control}
                name="qty_unit"
                {...controllerProps({ isRequired: true })}
                render={({ field }) => (
                    <FormField headless wide label="Qty Unit">
                        <WSelect
                            isRequired
                            isDisabled={isEditMode}
                            getKey={(item) => item.id}
                            items={qtyUnitItems}
                            aria-label="Select qty unit"
                            getTextValue={(item) => item.title}
                            valueKey={field.value}
                            onChange={(item) => field.onChange(item.id)}
                        >
                            {({ title }) => title}
                        </WSelect>
                    </FormField>
                )}
            />

            <Controller
                control={control}
                name="start_date"
                {...controllerProps({ isRequired: true })}
                render={({ field }) => (
                    <FormField isRequired headless label="Start Date" className="mt-4">
                        <WDateField
                            isRequired
                            isReadOnly={isEditMode}
                            aria-label="Start date"
                            placeholderValue={placeholder}
                            minValue={isEditMode ? undefined : placeholder}
                            value={field.value}
                            onChange={field.onChange}
                        />
                    </FormField>
                )}
            />

            <Controller
                control={control}
                name="end_date"
                render={({ field }) => (
                    <FormField
                        // TODO: Allow clearing end date (for "infinite" products)
                        headless
                        label="End Date"
                    >
                        <WDateField
                            autoFocus={isEditMode}
                            aria-label="End Date"
                            // TODO: Validate that new end date is not in the past?
                            // NB: Placeholder *must* be set to a `CalendarDateTime` object, otherwise the time input will not be displayed when the field is empty
                            placeholderValue={placeholder}
                            value={field.value}
                            onChange={field.onChange}
                        />
                    </FormField>
                )}
            />
        </WModalForm>
    );
}
